import { useState, useEffect, useMemo } from "react";
import { Heart, ArrowLeft } from "lucide-react";
import { Button } from "@/app/components/button";
import type { Ad } from "@/app/components/ad-card";
import { BottomNav } from "./bottom-nav";
import { formatCurrency } from "@/config/locale";
import { AdImage } from "@/app/components/common/ad-image";
import { enrichAdCategoryFromCatalog, getAdImage } from "@/app/utils/ad-utils";
import * as authClient from "@/app/api/auth-client";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";

function formatDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Agora mesmo";
  if (diffMins < 60) return `Há ${diffMins} ${diffMins === 1 ? "minuto" : "minutos"}`;
  if (diffHours < 24) return `Há ${diffHours} ${diffHours === 1 ? "hora" : "horas"}`;
  if (diffDays < 7) return `Há ${diffDays} ${diffDays === 1 ? "dia" : "dias"}`;

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export interface FavoriteItem {
  ad: Ad;
  favoritedAt: Date;
}

interface FavoritesPageProps {
  onBack: () => void;
  favoriteItems: FavoriteItem[];
  /** When provided, page fetches ad details for each id and builds the list (API-backed favorites). */
  favoriteAdIds?: string[];
  favoriteCreatedAt?: Record<string, string>;
  onRemoveFavorite: (adId: string) => void;
  onToggleFavorite?: (adId: string) => void;
  onAddToCart?: (adId: string) => void;
  onRemoveFromCart?: (adId: string) => void;
  isInCart?: (adId: string) => boolean;
  onViewAdDetail?: (adId: string) => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export function FavoritesPage({ onBack, favoriteItems, favoriteAdIds = [], favoriteCreatedAt = {}, onRemoveFavorite, onToggleFavorite, onAddToCart, onRemoveFromCart, isInCart, onViewAdDetail, activeTab, onTabChange, favoritesCount, onMenuClick, onPostAdClick, postAdBlockedByUnverifiedEmail = false }: FavoritesPageProps) {
  const [selectedAd, setSelectedAd] = useState<Ad | null>(null);
  /** When using API-backed favorites (favoriteAdIds), we fetch ad details and store here. */
  const [loadedItems, setLoadedItems] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(false);

  const useApiFavorites = favoriteAdIds.length > 0;

  useEffect(() => {
    if (!useApiFavorites) return;
    let cancelled = false;
    setLoading(true);
    authClient
      .getCategories()
      .catch(() => [] as authClient.Category[])
      .then((catalog) =>
        Promise.all(
          favoriteAdIds.map(async (adId) => {
            try {
              const api = await authClient.getAdById(adId);
              const ad = enrichAdCategoryFromCatalog(authClient.apiAdToAd(api), catalog);
              const createdAt = favoriteCreatedAt[adId];
              return { ad, favoritedAt: createdAt ? new Date(createdAt) : new Date() } as FavoriteItem;
            } catch {
              return null;
            }
          })
        )
      )
      .then((results) => {
      if (cancelled) return;
      setLoadedItems(results.filter((r): r is FavoriteItem => r !== null));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [useApiFavorites, favoriteAdIds.join(",")]);

  /** Show API-backed favorites when available; otherwise in-memory favoriteItems. */
  const displayItems = useApiFavorites ? loadedItems : favoriteItems;
  const sortedFavorites = useMemo(
    () => [...displayItems].sort((a, b) => b.favoritedAt.getTime() - a.favoritedAt.getTime()),
    [displayItems]
  );

  const isFavorite = (adId: string) => {
    return displayItems.some(item => item.ad.id === adId);
  };

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-center gap-4 px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Meus favoritos</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {useApiFavorites && loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-16">
            <p className="text-muted-foreground">Carregando favoritos…</p>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-10 h-10 text-secondary-foreground" />
            </div>
            <h3 className="mb-2">Nenhum favorito ainda</h3>
            <p className="text-muted-foreground mb-6">
              Comece a favoritar itens que você gosta para vê-los aqui
            </p>
            <Button onClick={onBack}>
              Explorar Anúncios
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedFavorites.map(({ ad, favoritedAt }) => (
              <FavoriteListItem
                key={ad.id}
                ad={ad}
                favoritedAt={favoritedAt}
                onRemoveFavorite={onRemoveFavorite}
                onToggleFavorite={onToggleFavorite}
                isFavorite={isFavorite}
                setSelectedAd={setSelectedAd}
                onAddToCart={onAddToCart}
                onRemoveFromCart={onRemoveFromCart}
                isInCart={isInCart}
                onViewAdDetail={onViewAdDetail}
              />
            ))}
          </div>
        )}
      </div>

      {/* Ad Detail Modal */}
      {selectedAd && (
        <AdDetailModal
          ad={selectedAd}
          isOpen={true}
          onClose={() => setSelectedAd(null)}
          isFavorite={isFavorite(selectedAd.id)}
          onToggleFavorite={onToggleFavorite}
          isInCart={isInCart ? isInCart(selectedAd.id) : false}
          onAddToCart={onAddToCart}
          onRemoveFromCart={onRemoveFromCart}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => onTabChange(tab as "home" | "favorites")}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}

interface FavoriteListItemProps {
  ad: Ad;
  favoritedAt: Date;
  onRemoveFavorite: (adId: string) => void;
  onToggleFavorite?: (adId: string) => void;
  isFavorite: (adId: string) => boolean;
  setSelectedAd: (ad: Ad | null) => void;
  onAddToCart?: (adId: string) => void;
  onRemoveFromCart?: (adId: string) => void;
  isInCart?: (adId: string) => boolean;
  onViewAdDetail?: (adId: string) => void;
}

function FavoriteListItem({ ad, favoritedAt, onRemoveFavorite, setSelectedAd, onAddToCart, onRemoveFromCart, isInCart, onViewAdDetail, onToggleFavorite: _onToggleFavorite, isFavorite: _isFavorite }: FavoriteListItemProps) {
  const accountNav = useAccountNavEnabled();
  return (
    <div className="bg-card p-4 hover:bg-accent/50 transition-colors">
      <div className="flex gap-4">
        {/* Product Image - Clickable */}
        <div 
          onClick={() => onViewAdDetail ? onViewAdDetail(ad.id) : setSelectedAd(ad)}
          className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden bg-secondary cursor-pointer hover:opacity-80 transition-opacity"
        >
          <AdImage
            src={getAdImage(ad) || ""}
            alt={ad.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Product Details - Clickable */}
        <div 
          onClick={() => onViewAdDetail ? onViewAdDetail(ad.id) : setSelectedAd(ad)}
          className="flex-1 min-w-0 cursor-pointer"
        >
          <h3 className="text-sm sm:text-base line-clamp-2 mb-1 hover:text-primary transition-colors">
            {ad.title}
          </h3>
          
          <p className="text-xs text-muted-foreground mb-2">
            Por {ad.location}
          </p>

          <p className="text-xs text-muted-foreground mb-2">
            Favoritado {formatDate(favoritedAt)}
          </p>

          <div className="mb-2">
            <p className="text-lg sm:text-xl font-medium text-primary">
              {formatCurrency(ad.price)}
            </p>
          </div>

          <p className="text-xs text-muted-foreground mb-3">
            Categoria: {ad.category}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onRemoveFavorite(ad.id)}
              className="px-4 py-1.5 text-xs sm:text-sm text-primary hover:bg-primary/10 rounded-md transition-colors border border-primary"
            >
              Excluir
            </button>
            {accountNav && onAddToCart && onRemoveFromCart && isInCart && (
              <button
                onClick={() => isInCart(ad.id) ? onRemoveFromCart(ad.id) : onAddToCart(ad.id)}
                className={`px-4 py-1.5 text-xs sm:text-sm ${isInCart(ad.id) ? 'text-red-500 hover:bg-red-500/10 border-red-500' : 'hover:bg-[rgba(249,115,22,0.1)] border-[var(--orange)]'} rounded-md transition-all duration-300 border`}
                style={!isInCart(ad.id) ? { color: 'var(--orange)' } : undefined}
              >
                {isInCart(ad.id) ? 'Remover do Carrinho' : 'Adicionar ao Carrinho'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface AdDetailModalProps {
  ad: Ad;
  isOpen: boolean;
  onClose: () => void;
  isFavorite: boolean;
  onToggleFavorite?: (id: string) => void;
  isInCart: boolean;
  onAddToCart?: (id: string) => void;
  onRemoveFromCart?: (id: string) => void;
}

function AdDetailModal({
  ad,
  isOpen,
  onClose,
  isFavorite,
  onToggleFavorite,
  isInCart,
  onAddToCart,
  onRemoveFromCart,
}: AdDetailModalProps) {
  const accountNav = useAccountNavEnabled();
  if (!isOpen) return null;

  const handleToggleCart = () => {
    if (isInCart && onRemoveFromCart) {
      onRemoveFromCart(ad.id);
    } else if (!isInCart && onAddToCart) {
      onAddToCart(ad.id);
    }
  };

  const handleToggleFavorite = () => {
    if (onToggleFavorite) onToggleFavorite(ad.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
      <div className="w-full max-w-2xl bg-card rounded-t-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Detalhes do Produto</h2>
          <div className="flex items-center gap-2">
            {accountNav && onToggleFavorite && (
              <button
                type="button"
                onClick={handleToggleFavorite}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
                aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
              >
                {isFavorite ? "❤️" : "🤍"}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Image */}
          <div className="w-full aspect-video bg-secondary rounded-lg overflow-hidden">
            <AdImage
              src={getAdImage(ad) || ""}
              alt={ad.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Title and Price */}
          <div>
            <h3 className="text-2xl mb-3">{ad.title}</h3>
            <p className="text-3xl text-primary font-medium">
              {formatCurrency(ad.price)}
            </p>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>📍</span>
              <span>{ad.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>🕒</span>
              <span>{ad.createdAt}</span>
            </div>
          </div>

          {/* Category */}
          <div>
            <span className="inline-block px-4 py-2 bg-secondary text-secondary-foreground text-sm rounded-full">
              {ad.category}
            </span>
          </div>

          {/* Description */}
          <div className="border-t border-border pt-6">
            <h4 className="mb-3 font-medium">Descrição</h4>
            <p className="text-muted-foreground leading-relaxed">
              {ad.description}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-4">
            {accountNav && (onAddToCart || onRemoveFromCart) && (
              <Button
                onClick={handleToggleCart}
                className={`w-full py-6 text-lg flex items-center justify-center gap-2 transition-all duration-300 ${
                  isInCart 
                    ? 'border-2' 
                    : 'shadow-lg hover:shadow-xl hover:scale-[1.02]'
                }`}
                style={
                  isInCart
                    ? {
                        backgroundColor: 'transparent',
                        borderColor: 'var(--orange)',
                        color: 'var(--orange)',
                      }
                    : {
                        backgroundColor: 'var(--orange)',
                        color: 'var(--orange-foreground)',
                      }
                }
                onMouseEnter={(e) => {
                  if (!isInCart) {
                    e.currentTarget.style.backgroundColor = 'var(--orange-hover)';
                  } else {
                    e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isInCart) {
                    e.currentTarget.style.backgroundColor = 'var(--orange)';
                  } else {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                variant={isInCart ? "outline" : "default"}
              >
                {isInCart ? "No Carrinho ✓" : "Adicionar ao Carrinho"}
              </Button>
            )}
            <Button className="w-full py-6 text-lg">
              Entrar em Contato
            </Button>
            <Button onClick={onClose} variant="outline" className="w-full py-6 text-lg">
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}